import React, { SyntheticEvent, useEffect, useMemo } from "react";

import moment from "moment";
import { useIntl } from "react-intl";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

import { formatDate } from "react-lib/utils";

import CardBillingEncountersUX from "./CardBillingEncountersUX";

import { DOCTOR_ORDER_LIMIT } from "./BILInterface";

type StatusKey = keyof typeof statusName;
type PaymentStatusKey = keyof typeof paymentStatusName;

const statusName = {
  APPOINTMENT: "เป็นคำสั่งล่วงหน้า",
  PENDING: "กำลังทำคำสั่ง",
  PERFORMED: "ทำคำสั่งเรียบร้อยแล้ว",
  CANCEL: "คำสั่งถูกยกเลิก",
  DRAFT: "คำสั่ง DRAFT",
  PLANNING: "คำสั่งแบบต่อเนื่อง",
  OFF: "หยุดทำคำสั่งแบบต่อเนื่อง",
} as const;

const paymentStatusName = {
  PENDING: "รอดำเนินการ",
  READY: "พร้อมชำระเงิน",
  PAID: "ชำระเงิน",
  PARTIAL_PAID: "ชำระเงินบางส่วน",
} as const;

const BUTTON_ACTIONS = {
  search: "SEARCH",
  refresh: "REFRESH",
};

const CARD_BILLING_ENCOUNTERS = "CardBillingEncounters";

const ACTION_SEARCH = `${CARD_BILLING_ENCOUNTERS}_${BUTTON_ACTIONS.search}`;
const ACTION_REFRESH = `${CARD_BILLING_ENCOUNTERS}_${BUTTON_ACTIONS.refresh}`;

const CardBillingEncounters = (props: any) => {
  const intl = useIntl();

  useEffect(() => {
    props.onEvent({
      message: "GetBillingEncounterOrder",
      params: { btnAction: ACTION_SEARCH, initial: true },
    });

    return () => {
      props.setProp("billingEncounterOrder", {
        ...props.billingEncounterOrder,
        doctorOrder: [],
        encounterList: [],
        filterDrOrder: {},
      });
    };
  }, []);

  const checkNote = (expired: any, payment: any, order: any) => {
    if (expired === true && payment === "READY" && order === "PERFORMED") {
      return <div style={{ color: "red" }}>{intl.formatMessage({ id: "ค้างชำระ" })}</div>;
    } else if (payment === "READY" && order === "PENDING") {
      return <div>{intl.formatMessage({ id: "รอชำระเงิน" })}</div>;
    } else if (payment === "PENDING" && order === "PERFORMED") {
      return <div>{intl.formatMessage({ id: "รอสรุปค่าใช้จ่าย" })}</div>;
    } else if (payment === "PENDING" && order === "PENDING") {
      return <div>{intl.formatMessage({ id: "รอหน่วยบริการ" })}</div>;
    } else if (payment === "PAID" && order === "PENDING") {
      return <div style={{ color: "orange" }}>{intl.formatMessage({ id: "รอรับบริการ" })}</div>;
    } else {
      return <div></div>;
    }
  };

  const statusOptions = useMemo(() => {
    const options = Object.entries(statusName).map(([key, value]) => ({
      key,
      text: value,
      value: key,
    }));

    return [{ key: "ALL", text: "ทุกสถานะ", value: "ALL" }, ...options];
  }, []);

  const paymentStatusOptions = useMemo(() => {
    const options = Object.entries(paymentStatusName).map(([key, value]) => ({
      key,
      text: value,
      value: key,
    }));

    return [{ key: "ALL", text: "ทุกสถานะ", value: "ALL" }, ...options];
  }, []);

  const drOrderSubclassOptions = useMemo(() => {
    const options = props.billingEncounterOrder?.drOrderSubclassOptions ?? [];

    return [{ key: "ALL", text: "ทุกประเภท", value: "ALL" }, ...options];
  }, [props.billingEncounterOrder?.drOrderSubclassOptions]);

  const encounterListData = useMemo(() => {
    return (props.billingEncounterOrder?.encounterList?.items || []).map(
      (item: any, index: number) => ({
        ...item,
      })
    );
  }, [props.billingEncounterOrder?.encounterList]);

  const doctorOrderData = useMemo(() => {
    return (props.billingEncounterOrder?.doctorOrder?.items || []).map((item: any) => ({
      ...item,
      order_time: (
        <div>
          {formatDate(moment(item?.order_time))} [{moment(item?.order_time).format("HH:mm")}]
        </div>
      ),
      payment_status_name: (
        <div>
          {paymentStatusName[item?.payment_status_name as PaymentStatusKey] ||
            item?.payment_status_name}
        </div>
      ),
      order_status_name: (
        <div>{statusName[item?.order_status_name as StatusKey] || item?.order_status_name}</div>
      ),
      note: (
        <div>{checkNote(item?.is_expired, item?.payment_status_name, item?.order_status_name)}</div>
      ),
      encounter_number: (
        <div style={{ display: "flex", justifyContent: "center" }}>{item.encounter_number}</div>
      ),
    }));
  }, [props.billingEncounterOrder?.doctorOrder]);

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;
    const value = data.type === "checkbox" ? data.checked : data.value;

    if (!name) {
      return;
    }

    props.setProp("billingEncounterOrder.filterDrOrder", {
      ...props.billingEncounterOrder?.filterDrOrder,
      [name]: value,
    });
  };

  const handleRefresh = () => {
    props.onEvent({
      message: "GetBillingEncounterOrder",
      params: { btnAction: ACTION_REFRESH },
    });
  };

  const handleSearch = () => {
    props.onEvent({
      message: "GetBillingEncounterOrder",
      params: { btnAction: ACTION_SEARCH },
    });
  };

  const handlePageChange = (activePage: number) => {
    props.onEvent({
      message: "GetBillingEncounterOrder",
      params: { activePage, btnAction: ACTION_SEARCH },
    });
  };

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <CardBillingEncountersUX
        doctorOrderData={doctorOrderData}
        encounterListData={encounterListData}
        filterDrOrder={props.billingEncounterOrder?.filterDrOrder}
        languageUX={props.languageUX}
        drOrderSubclassOptions={drOrderSubclassOptions}
        paymentStatusOptions={paymentStatusOptions}
        statusOptions={statusOptions}
        onChangeFilter={handleChangeFilter}
        buttonRefresh={
          <ButtonLoadCheck
            setProp={props.setProp}
            icon="redo"
            paramKey={ACTION_REFRESH}
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_REFRESH]}
            onClick={handleRefresh}
          />
        }
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={ACTION_SEARCH}
            title="ค้นหา"
            fluid
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_SEARCH]}
            onClick={handleSearch}
          />
        }
        pagination={
          <PaginationShorthand
            activePage={props.billingEncounterOrder?.doctorOrder?.activePage ?? 1}
            limit={DOCTOR_ORDER_LIMIT}
            total={props.billingEncounterOrder?.doctorOrder?.total}
            onPageChange={handlePageChange}
          />
        }
      />
    </div>
  );
};

export default CardBillingEncounters;
